import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { PlayCircle, FileText, Clock, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const HistoryPage = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessions(response.data || []);
    } catch (error) {
      toast.error('Fehler beim Laden der Historie');
    } finally {
      setLoading(false);
    }
  };

  const handleResumeSession = (sessionId, status) => {
    if (status === 'active') {
      navigate(`/diagnosis/session/${sessionId}`);
    } else {
      toast.info('Diese Session ist bereits abgeschlossen');
    }
  };

  const handleViewReport = async (sessionId) => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        toast.error('Nicht angemeldet');
        return;
      }
      
      // First generate the report (if not already generated)
      try {
        await axios.post(`${API}/sessions/${sessionId}/report`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (generateError) {
        // Ignore if PDF already exists
        console.log('PDF generation response:', generateError.response?.status);
      }
      
      // Then download it using the download endpoint
      const downloadResponse = await axios.get(`${API}/sessions/${sessionId}/report/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      if (!downloadResponse.data) {
        throw new Error('Keine PDF-Daten erhalten');
      }
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([downloadResponse.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Diagnosebericht_${sessionId}.pdf`);
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      toast.success('PDF wird heruntergeladen');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Fehler beim Laden des Reports';
      toast.error(errorMsg);
      console.error('PDF download error:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
    }
  };

  return (
    <div className="min-h-screen p-8" style={{ backgroundColor: '#0B0F14' }}>
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl font-bold mb-8" style={{ color: '#E6EDF3', fontFamily: 'Manrope' }}>
          Diagnose-Historie
        </h2>

        {loading ? (
          <p style={{ color: '#94A3B8' }}>Laden...</p>
        ) : sessions.length === 0 ? (
          <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
            <CardContent className="p-8 text-center">
              <Clock className="w-16 h-16 mx-auto mb-4" style={{ color: '#94A3B8' }} />
              <p style={{ color: '#94A3B8' }}>Noch keine Diagnosen vorhanden</p>
              <Button
                onClick={() => navigate('/diagnosis/new')}
                className="mt-4"
                style={{ backgroundColor: '#1EA0E6', color: '#ffffff' }}
              >
                Erste Diagnose starten
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {sessions.map((session) => (
              <Card key={session.id} style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <CardTitle style={{ color: '#E6EDF3', fontSize: '1.2rem' }}>
                        {session.symptom_text}
                      </CardTitle>
                      <p className="text-sm mt-2" style={{ color: '#94A3B8' }}>
                        {new Date(session.created_at).toLocaleDateString('de-DE', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <span
                      className="px-3 py-1 rounded-full text-sm flex items-center gap-2"
                      style={{
                        backgroundColor: session.status === 'done' ? '#22C55E20' : session.status === 'active' ? '#F59E0B20' : '#EF444420',
                        color: session.status === 'done' ? '#22C55E' : session.status === 'active' ? '#F59E0B' : '#EF4444'
                      }}
                    >
                      {session.status === 'done' && <CheckCircle className="w-4 h-4" />}
                      {session.status === 'active' && <PlayCircle className="w-4 h-4" />}
                      {session.status === 'done' ? 'Abgeschlossen' : session.status === 'active' ? 'In Bearbeitung' : 'Abgebrochen'}
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-3">
                    {session.status === 'active' && (
                      <Button
                        onClick={() => handleResumeSession(session.id, session.status)}
                        style={{ backgroundColor: '#1EA0E6', color: '#ffffff' }}
                      >
                        <PlayCircle className="w-4 h-4 mr-2" />
                        Fortsetzen
                      </Button>
                    )}
                    {session.status === 'done' && (
                      <>
                        <Button
                          onClick={() => navigate(`/report/${session.id}`)}
                          style={{ backgroundColor: '#1EA0E6', color: '#ffffff' }}
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          Detaillierter Report
                        </Button>
                        <Button
                          onClick={() => handleViewReport(session.id)}
                          variant="outline"
                          style={{ borderColor: '#1F2937', color: '#E6EDF3' }}
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          PDF Download
                        </Button>
                      </>
                    )}
                    <Button
                      onClick={() => navigate(`/diagnosis/session/${session.id}`)}
                      variant="outline"
                      style={{ borderColor: '#1F2937', color: '#E6EDF3' }}
                    >
                      Details ansehen
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};